import { Button, ButtonVariants } from '@components/button/Button';
import { Modal } from '@components/modal/Modal';
import { ModalActions } from '@components/modal/ModalActions';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { NavigationWarningModal } from '@shared/components/modal/NavigationWarningModal';
import { SimpleBar } from '@shared/components/simpleBar/simpleBar';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { reportAppError } from '@shared/reportAppError';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { type Location, useBlocker } from 'react-router-dom';
import { SettingsOutletHeader } from '../shared/SettingsOutletHeader';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/useRestaurant';
import { type AdminRestaurant, getAdminRestaurant } from '../team/apiHelpers';
import { saveRestaurantSettings } from './apiHelpers';
import { AddressSection } from './addressSection/AddressSection';
import { AvailabilitiesSection } from './availabilitiesSection/AvailabilitiesSection';
import { CancellationPolicySection } from './cancellationPolicySection/CancellationPolicySection';
import { CoverLimitSection } from './coverLimitSection/CoverLimitSection';
import { DetailsSection } from './detailsSection/DetailsSection';
import {
  buildCancellationPolicyPayloadForSave,
  buildRestaurantSettingsPayload,
  restaurantToFormValues,
} from './utils/generalSettingsFormHelpers';
import { type GeneralSettingsForm } from './utils/generalSettingsFormTypes';
import styles from './GeneralPage.scss';
import { evaluateMaxGuestListingConflict } from './utils/maxGuestCountListingConflict';
import { MaxGuestCountConflictModal } from './maxGuestCountSection/MaxGuestCountConflictModal';
import { MaxGuestCountSection } from './maxGuestCountSection/MaxGuestCountSection';
import { PacingControlsSection } from './pacingControlsSection/PacingControlsSection';
import { ProcessingFeeSection } from './processingFeeSection/ProcessingFeeSection';
import { RestaurantConfigurationSection } from './restaurantConfigurationSection/RestaurantConfigurationSection';
import {
  type RestaurantHeroImages,
  restaurantHeroImagesQueryKey,
} from './restaurantConfigurationSection/apiHelpers';
import { ScheduleSection } from './scheduleSection/ScheduleSection';
import { useGeneralSettingsFormScroll } from './useGeneralSettingsFormScroll';

export const GeneralPage = () => {
  const [restaurant, setRestaurant] = useState<AdminRestaurant>();
  const [isSaving, setIsSaving] = useState(false);
  const { refreshRestaurants: authRefreshRestaurants, isManager } = useAuth();
  const selectedRestaurant = useRestaurant();
  const queryClient = useQueryClient();
  const sectionId = useId();
  const pendingSaveAfterMaxGuestConflictRef =
    useRef<GeneralSettingsForm | null>(null);
  const pendingSaveAfterCancellationDeleteRef =
    useRef<GeneralSettingsForm | null>(null);
  const shouldProceedNavigationAfterPersistRef = useRef(false);
  const generalSettingsSubmitInFlightRef = useRef(false);
  const {
    isOpen: isMaxGuestConflictModalOpen,
    open: openMaxGuestConflictModal,
    close: closeMaxGuestConflictModalBase,
  } = useIsOpen();
  const {
    isOpen: isCancellationDeleteModalOpen,
    open: openCancellationDeleteModal,
    close: closeCancellationDeleteModalBase,
  } = useIsOpen();
  const {
    isOpen: isCoverLimitRemoveModalOpen,
    open: openCoverLimitRemoveModal,
    close: closeCoverLimitRemoveModalBase,
  } = useIsOpen();
  const {
    isOpen: isPacingControlsRemoveModalOpen,
    open: openPacingControlsRemoveModal,
    close: closePacingControlsRemoveModalBase,
  } = useIsOpen();
  const {
    isOpen: isCancellationPolicyRemoveConfirmModalOpen,
    open: openCancellationPolicyRemoveConfirmModal,
    close: closeCancellationPolicyRemoveConfirmModalBase,
  } = useIsOpen();

  const formMethods = useForm<GeneralSettingsForm>({
    defaultValues: {
      heroImageKeys: [],
    },
  });
  const { getValues, handleSubmit, reset, setValue, formState } = formMethods;
  const { isDirty } = formState;

  const blocker = useBlocker(
    useCallback(
      ({
        currentLocation,
        nextLocation,
      }: {
        currentLocation: Location;
        nextLocation: Location;
      }) => isDirty && currentLocation.pathname !== nextLocation.pathname,
      [isDirty],
    ),
  );

  const clearNavigationProceedIntent = useCallback(() => {
    shouldProceedNavigationAfterPersistRef.current = false;
  }, []);

  const completeBlockedNavigationIfNeeded = useCallback(() => {
    if (!shouldProceedNavigationAfterPersistRef.current) {
      return;
    }
    shouldProceedNavigationAfterPersistRef.current = false;
    if (blocker.state === 'blocked') {
      blocker.proceed?.();
    }
  }, [blocker]);

  const handleCloseMaxGuestConflictModal = () => {
    pendingSaveAfterMaxGuestConflictRef.current = null;
    clearNavigationProceedIntent();
    closeMaxGuestConflictModalBase();
  };

  const handleCloseCancellationDeleteModal = () => {
    pendingSaveAfterCancellationDeleteRef.current = null;
    clearNavigationProceedIntent();
    closeCancellationDeleteModalBase();
  };

  const handleCloseCancellationPolicyRemoveConfirmModal = () => {
    closeCancellationPolicyRemoveConfirmModalBase();
  };

  const handleConfirmRemoveCancellationPolicyFromForm = () => {
    setValue('cancellationPolicyEnabled', 0, { shouldDirty: true });
    closeCancellationPolicyRemoveConfirmModalBase();
  };

  const handleCloseCoverLimitRemoveModal = () => {
    closeCoverLimitRemoveModalBase();
  };

  const handleConfirmRemoveCoverLimit = () => {
    setValue('coverLimit', '', { shouldDirty: true });
    closeCoverLimitRemoveModalBase();
  };

  const handleClosePacingControlsRemoveModal = () => {
    closePacingControlsRemoveModalBase();
  };

  const handleConfirmRemovePacingControls = () => {
    setValue('pacingEnabled', 0, { shouldDirty: true });
    closePacingControlsRemoveModalBase();
  };

  const fetchAdminRestaurant = useCallback(async () => {
    try {
      const data = await getAdminRestaurant(selectedRestaurant.id);
      setRestaurant(data);
    } catch (error) {
      reportAppError(error);
    }
  }, [selectedRestaurant.id]);

  useEffect(() => {
    if (!isManager) {
      return;
    }
    void fetchAdminRestaurant();
  }, [fetchAdminRestaurant, isManager]);

  useEffect(() => {
    if (!isManager || !restaurant) {
      return;
    }
    reset(restaurantToFormValues(restaurant, getValues('heroImageKeys') ?? []));
  }, [restaurant, reset, isManager, getValues]);

  const { bodyScrollHostRef, bodyScrollHostStyle } =
    useGeneralSettingsFormScroll({ isManager, restaurant });

  const syncHeroImagesQueryCache = (
    restaurantId: string,
    data: GeneralSettingsForm,
  ) => {
    queryClient.setQueryData<RestaurantHeroImages>(
      restaurantHeroImagesQueryKey(restaurantId),
      { imageKeys: data.heroImageKeys ?? [] },
    );
  };

  const runPersistAfterGeneralAndMax = async (
    data: GeneralSettingsForm,
    snapshot: AdminRestaurant,
  ): Promise<void> => {
    const cancellationPolicy = buildCancellationPolicyPayloadForSave(
      data,
      snapshot,
    );
    await saveRestaurantSettings(
      snapshot.id,
      buildRestaurantSettingsPayload(data, cancellationPolicy),
    );
    syncHeroImagesQueryCache(snapshot.id, data);
  };

  const submit = handleSubmit(
    async (data) => {
      if (generalSettingsSubmitInFlightRef.current) {
        return;
      }
      generalSettingsSubmitInFlightRef.current = true;
      try {
        if (!restaurant) {
          return;
        }

        const listingConflictEvaluation = await evaluateMaxGuestListingConflict(
          restaurant.id,
          restaurant.maxReservationGuests,
          data.maxGuestCount,
        );

        if (listingConflictEvaluation.outcome === 'fetchFailed') {
          errorToast({
            message: 'Failed to fetch listings. Please try again.',
          });
          reportAppError(listingConflictEvaluation.error);
          clearNavigationProceedIntent();
          return;
        }

        if (listingConflictEvaluation.outcome === 'listingConflict') {
          pendingSaveAfterMaxGuestConflictRef.current = data;
          openMaxGuestConflictModal();
          return;
        }

        const turningOffCancellationPolicy =
          data.cancellationPolicyEnabled === 0 &&
          !!restaurant.cancellationPolicy;
        if (turningOffCancellationPolicy) {
          pendingSaveAfterCancellationDeleteRef.current = data;
          openCancellationDeleteModal();
          return;
        }

        setIsSaving(true);
        let didPersistSucceed = false;
        try {
          await runPersistAfterGeneralAndMax(data, restaurant);
          authRefreshRestaurants();
          const navigateAwayAfterSave =
            shouldProceedNavigationAfterPersistRef.current;
          if (navigateAwayAfterSave) {
            // Proceed before `fetchAdminRestaurant` + `reset()` clears `isDirty`,
            // which would otherwise unblock navigation without applying it.
            completeBlockedNavigationIfNeeded();
          }
          await fetchAdminRestaurant();
          didPersistSucceed = true;
        } catch (error) {
          errorToast({ message: 'Could not save general settings' });
          reportAppError(error);
          clearNavigationProceedIntent();
        } finally {
          setIsSaving(false);
        }
        if (didPersistSucceed) {
          successToast({ message: 'General settings saved' });
          try {
            completeBlockedNavigationIfNeeded();
          } catch (navigationError) {
            reportAppError(navigationError);
          }
        }
      } finally {
        generalSettingsSubmitInFlightRef.current = false;
      }
    },
    () => {
      clearNavigationProceedIntent();
    },
  );

  const handleMaxGuestConflictContinue = async () => {
    if (generalSettingsSubmitInFlightRef.current) {
      return;
    }
    const data = pendingSaveAfterMaxGuestConflictRef.current;
    pendingSaveAfterMaxGuestConflictRef.current = null;
    if (!restaurant || !data) {
      return;
    }
    generalSettingsSubmitInFlightRef.current = true;
    try {
      setIsSaving(true);
      let didPersistSucceed = false;
      try {
        await runPersistAfterGeneralAndMax(data, restaurant);
        authRefreshRestaurants();
        const navigateAwayAfterSave =
          shouldProceedNavigationAfterPersistRef.current;
        if (navigateAwayAfterSave) {
          // See equivalent branch in `submit`.
          completeBlockedNavigationIfNeeded();
        }
        await fetchAdminRestaurant();
        didPersistSucceed = true;
      } catch (error) {
        errorToast({ message: 'Could not save general settings' });
        reportAppError(error);
        clearNavigationProceedIntent();
      } finally {
        setIsSaving(false);
      }
      if (didPersistSucceed) {
        successToast({ message: 'General settings saved' });
        try {
          completeBlockedNavigationIfNeeded();
        } catch (navigationError) {
          reportAppError(navigationError);
        }
      }
    } finally {
      generalSettingsSubmitInFlightRef.current = false;
    }
  };

  const handleCancellationDeleteContinue = async () => {
    if (generalSettingsSubmitInFlightRef.current) {
      return;
    }
    const data = pendingSaveAfterCancellationDeleteRef.current;
    pendingSaveAfterCancellationDeleteRef.current = null;
    if (!restaurant || !data || !restaurant.cancellationPolicy) {
      return;
    }
    generalSettingsSubmitInFlightRef.current = true;
    try {
      setIsSaving(true);
      let didPersistSucceed = false;
      try {
        await saveRestaurantSettings(
          restaurant.id,
          buildRestaurantSettingsPayload(data, {
            action: 'delete',
            cancellationPolicyId: restaurant.cancellationPolicy.id,
          }),
        );
        syncHeroImagesQueryCache(restaurant.id, data);
        authRefreshRestaurants();
        const navigateAwayAfterSave =
          shouldProceedNavigationAfterPersistRef.current;
        if (navigateAwayAfterSave) {
          // See equivalent branch in `submit`.
          completeBlockedNavigationIfNeeded();
        }
        await fetchAdminRestaurant();
        didPersistSucceed = true;
      } catch (error) {
        errorToast({ message: 'Could not save general settings' });
        reportAppError(error);
        clearNavigationProceedIntent();
      } finally {
        setIsSaving(false);
      }
      if (didPersistSucceed) {
        successToast({ message: 'General settings saved' });
        try {
          completeBlockedNavigationIfNeeded();
        } catch (navigationError) {
          reportAppError(navigationError);
        }
      }
    } finally {
      generalSettingsSubmitInFlightRef.current = false;
    }
  };

  if (!isManager) {
    return null;
  }

  if (!restaurant) {
    return <div className={styles.root} />;
  }

  return (
    <div className={styles.root}>
      <SettingsOutletHeader
        title="General"
        actions={
          <Button
            isDisabled={!isDirty || isSaving}
            isLoading={isSaving}
            label="Save"
            onClick={() => {
              clearNavigationProceedIntent();
              void submit();
            }}
            variant={ButtonVariants.Primary}
          />
        }
      />

      <div
        ref={bodyScrollHostRef}
        className={styles.bodyScrollHost}
        style={bodyScrollHostStyle}
      >
        <SimpleBar className={styles.bodyScroll}>
          <FormProvider {...formMethods}>
            <form
              className={styles.form}
              onSubmit={(event) => {
                event.preventDefault();
                clearNavigationProceedIntent();
                void submit();
              }}
            >
              <DetailsSection
                restaurant={restaurant}
                sectionTitleId={`${sectionId}-details-heading`}
              />
              <AddressSection
                restaurant={restaurant}
                sectionTitleId={`${sectionId}-address-heading`}
              />
              <ScheduleSection
                sectionTitleId={`${sectionId}-schedule-heading`}
              />
              <AvailabilitiesSection
                sectionTitleId={`${sectionId}-avail-heading`}
              />
              <MaxGuestCountSection
                restaurant={restaurant}
                sectionTitleId={`${sectionId}-max-guest-heading`}
              />
              <CancellationPolicySection
                restaurant={restaurant}
                sectionTitleId={`${sectionId}-cancellation-heading`}
                isSaving={isSaving}
                onRequestRemovePolicy={openCancellationPolicyRemoveConfirmModal}
              />
              <CoverLimitSection
                restaurant={restaurant}
                sectionTitleId={`${sectionId}-cover-heading`}
                isSaving={isSaving}
                onRequestRemoveCoverLimit={openCoverLimitRemoveModal}
              />
              <PacingControlsSection
                restaurant={restaurant}
                sectionTitleId={`${sectionId}-pacing-heading`}
                isSaving={isSaving}
                onRequestRemovePacingControls={openPacingControlsRemoveModal}
              />
              <ProcessingFeeSection
                sectionTitleId={`${sectionId}-processing-fee-heading`}
              />
              <RestaurantConfigurationSection
                restaurantId={restaurant.id}
                sectionTitleId={`${sectionId}-restaurant-configuration-heading`}
              />
            </form>
          </FormProvider>
        </SimpleBar>
      </div>

      <MaxGuestCountConflictModal
        closeModal={handleCloseMaxGuestConflictModal}
        handleConfirm={() => {
          void handleMaxGuestConflictContinue();
        }}
        isOpen={isMaxGuestConflictModalOpen}
      />

      <Modal
        isOpen={isCancellationPolicyRemoveConfirmModalOpen}
        onClose={handleCloseCancellationPolicyRemoveConfirmModal}
        subtitle="You can turn the policy back on before saving if you change your mind. Save your settings to apply this change."
        title="Remove Policy"
      >
        <ModalActions>
          <Button
            label="Keep Policy"
            onClick={handleCloseCancellationPolicyRemoveConfirmModal}
            variant={ButtonVariants.Tertiary}
          />
          <Button
            label="Remove Policy"
            onClick={handleConfirmRemoveCancellationPolicyFromForm}
            variant={ButtonVariants.Primary}
          />
        </ModalActions>
      </Modal>

      <Modal
        isOpen={isCancellationDeleteModalOpen}
        onClose={handleCloseCancellationDeleteModal}
        subtitle="Existing reservations with a cancellation policy are not affected."
        title="Delete Cancellation Policy"
      >
        <ModalActions>
          <Button
            label="Keep Policy"
            onClick={handleCloseCancellationDeleteModal}
            variant={ButtonVariants.Tertiary}
          />
          <Button
            label="Delete Policy"
            onClick={() => {
              void handleCancellationDeleteContinue();
              closeCancellationDeleteModalBase();
            }}
            variant={ButtonVariants.Primary}
          />
        </ModalActions>
      </Modal>

      <Modal
        isOpen={isCoverLimitRemoveModalOpen}
        onClose={handleCloseCoverLimitRemoveModal}
        subtitle="You can enter a new limit before saving if you change your mind. Save your settings to apply this change."
        title="Remove Cover Limit"
      >
        <ModalActions>
          <Button
            label="Keep Cover Limit"
            onClick={handleCloseCoverLimitRemoveModal}
            variant={ButtonVariants.Tertiary}
          />
          <Button
            label="Remove Cover Limit"
            onClick={handleConfirmRemoveCoverLimit}
            variant={ButtonVariants.Primary}
          />
        </ModalActions>
      </Modal>

      <Modal
        isOpen={isPacingControlsRemoveModalOpen}
        onClose={handleClosePacingControlsRemoveModal}
        subtitle="You can turn pacing back on before saving if you change your mind. Save your settings to apply this change."
        title="Remove Pacing Controls"
      >
        <ModalActions>
          <Button
            label="Keep Pacing Controls"
            onClick={handleClosePacingControlsRemoveModal}
            variant={ButtonVariants.Tertiary}
          />
          <Button
            label="Remove Pacing Controls"
            onClick={handleConfirmRemovePacingControls}
            variant={ButtonVariants.Primary}
          />
        </ModalActions>
      </Modal>

      <NavigationWarningModal
        isOpen={blocker.state === 'blocked'}
        modalMessage={
          <p>
            You have unsaved changes to your general settings. Please save your
            work before navigating to a different page.
          </p>
        }
        onClose={() => {
          if (isSaving) {
            return;
          }
          clearNavigationProceedIntent();
          blocker.reset?.();
        }}
        handleLoseChangesClick={() => {
          clearNavigationProceedIntent();
          blocker.proceed?.();
        }}
        handleSaveChangesClick={() => {
          shouldProceedNavigationAfterPersistRef.current = true;
          void submit();
        }}
      />
    </div>
  );
};
